import React, { useState, useEffect } from 'react';
import { Filter, ArrowDown, ChevronRight, ChevronDown } from 'lucide-react';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

/**
 * MatomoFunnelChart - Affiche un entonnoir de conversion basé sur les événements Matomo
 */
const MatomoFunnelChart = ({
  events = [],
  formatNumber,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'matomo_funnel'
}) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [funnelSteps, setFunnelSteps] = useState([]);
  const [expandedStep, setExpandedStep] = useState(null);  // Étape cliquée pour voir les détails

  // Initialiser avec la première catégorie qui a des actions
  useEffect(() => {
    if (events.length > 0 && !selectedCategory) {
      const firstWithActions = events.find(cat => cat.actions && cat.actions.length > 1);
      if (firstWithActions) {
        setSelectedCategory(firstWithActions.label);
      }
    }
  }, [events, selectedCategory]);

  // Mettre à jour les étapes quand la catégorie change
  useEffect(() => {
    if (selectedCategory) {
      const category = events.find(cat => cat.label === selectedCategory);
      console.log('[FUNNEL] Catégorie sélectionnée:', selectedCategory, category);
      if (category && category.actions) {
        // Trier par nombre de visites décroissant par défaut
        const sortedActions = [...category.actions].sort((a, b) => b.nb_visits - a.nb_visits);
        console.log('[FUNNEL] Actions triées:', sortedActions);
        console.log('[FUNNEL] Première action names:', sortedActions[0]?.names);
        setFunnelSteps(sortedActions);
        setExpandedStep(null);  // Réinitialiser l'étape étendue
      }
    }
  }, [selectedCategory, events]);

  if (!events || events.length === 0) {
    return null;
  }

  // Filtrer les catégories avec au moins 2 actions
  const categoriesWithActions = events.filter(cat => cat.actions && cat.actions.length > 1);

  if (categoriesWithActions.length === 0) {
    return null;
  }

  // Calculer les métriques de l'entonnoir
  const firstStepVisits = funnelSteps.length > 0 ? funnelSteps[0].nb_visits : 0;

  // Couleurs pour l'entonnoir (du plus foncé au plus clair)
  const getStepColor = (index, total) => {
    const startHue = 38; // Orange
    const saturation = 90;
    const startLightness = 45;
    const endLightness = 75;
    const lightness = startLightness + ((endLightness - startLightness) * (index / Math.max(total - 1, 1)));
    return `hsl(${startHue}, ${saturation}%, ${lightness}%)`;
  };

  return (
    <div className="matomo-funnel-chart" style={{
      marginTop: '24px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#FAF9F5'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Filter size={20} />
          Entonnoir de conversion
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Sélecteur de catégorie */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#ffffff',
              fontSize: '0.9em',
              color: '#374151',
              cursor: 'pointer',
              minWidth: '200px'
            }}
          >
            {categoriesWithActions.map(cat => (
              <option key={cat.label} value={cat.label}>
                {cat.label} ({cat.actions.length} étapes)
              </option>
            ))}
          </select>
          {toggleClientModuleVisibility && (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Entonnoir de conversion"
            />
          )}
        </div>
      </div>

      {/* Contenu de l'entonnoir */}
      <div style={{ padding: '24px 20px' }}>
        {/* Étapes de l'entonnoir */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {funnelSteps.map((step, index) => {
            const conversionRate = firstStepVisits > 0
              ? ((step.nb_visits / firstStepVisits) * 100).toFixed(1)
              : 0;
            const dropOffRate = index > 0 && funnelSteps[index - 1].nb_visits > 0
              ? (((funnelSteps[index - 1].nb_visits - step.nb_visits) / funnelSteps[index - 1].nb_visits) * 100).toFixed(1)
              : 0;
            const barWidth = firstStepVisits > 0
              ? Math.max((step.nb_visits / firstStepVisits) * 100, 5)
              : 100;

            return (
              <div key={step.label}>
                {/* Indicateur de drop-off entre les étapes */}
                {index > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px 0',
                    color: '#ef4444',
                    fontSize: '0.8em'
                  }}>
                    <ArrowDown size={14} style={{ marginRight: '4px' }} />
                    <span>-{dropOffRate}% de perte</span>
                  </div>
                )}

                {/* Étape */}
                <div
                  onClick={() => setExpandedStep(expandedStep === index ? null : index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '8px',
                    margin: '-8px',
                    borderRadius: '8px',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {/* Indicateur d'expansion */}
                  <div style={{ width: '16px', flexShrink: 0 }}>
                    {step.names && step.names.length > 0 ? (
                      expandedStep === index ?
                        <ChevronDown size={16} style={{ color: '#6b7280' }} /> :
                        <ChevronRight size={16} style={{ color: '#6b7280' }} />
                    ) : null}
                  </div>

                  {/* Numéro d'étape */}
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: getStepColor(index, funnelSteps.length),
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    fontSize: '0.85em',
                    flexShrink: 0
                  }}>
                    {index + 1}
                  </div>

                  {/* Barre et informations */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <span style={{
                        fontWeight: '500',
                        color: '#1f2937',
                        fontSize: '0.95em'
                      }}>
                        {step.label}
                      </span>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <span style={{
                          fontSize: '0.9em',
                          color: '#6b7280'
                        }}>
                          <strong style={{ color: '#374151' }}>{formatNumber(step.nb_visits)}</strong> sessions
                        </span>
                        <span style={{
                          fontSize: '0.9em',
                          fontWeight: '600',
                          color: index === 0 ? '#059669' : conversionRate >= 50 ? '#059669' : conversionRate >= 20 ? '#d97706' : '#dc2626',
                          backgroundColor: index === 0 ? '#d1fae5' : conversionRate >= 50 ? '#d1fae5' : conversionRate >= 20 ? '#fef3c7' : '#fee2e2',
                          padding: '2px 8px',
                          borderRadius: '12px'
                        }}>
                          {conversionRate}%
                        </span>
                      </div>
                    </div>

                    {/* Barre de progression */}
                    <div style={{
                      height: '24px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: `${barWidth}%`,
                        height: '100%',
                        backgroundColor: getStepColor(index, funnelSteps.length),
                        borderRadius: '6px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                </div>

                {/* Sous-étapes (noms d'événements) */}
                {expandedStep === index && step.names && step.names.length > 0 && (
                  <div style={{
                    marginTop: '12px',
                    marginLeft: '56px',
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      fontSize: '0.85em',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '8px'
                    }}>
                      Détails ({step.names.length} sous-catégories)
                    </div>
                    {step.names.slice(0, 10).map((name, nameIndex) => (
                      <div
                        key={nameIndex}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 0',
                          borderBottom: nameIndex < Math.min(step.names.length, 10) - 1 ? '1px solid #e5e7eb' : 'none',
                          fontSize: '0.85em'
                        }}
                      >
                        <span style={{ color: '#374151' }}>{name.label}</span>
                        <span style={{ color: '#6b7280' }}>
                          <strong style={{ color: '#374151' }}>{formatNumber(name.nb_visits)}</strong> sessions
                        </span>
                      </div>
                    ))}
                    {step.names.length > 10 && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '0.8em',
                        color: '#9ca3af',
                        fontStyle: 'italic'
                      }}>
                        +{step.names.length - 10} autres
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Résumé */}
        {funnelSteps.length >= 2 && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #bbf7d0'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ color: '#166534', fontWeight: '500' }}>
                Taux de conversion global
              </span>
              <span style={{
                fontSize: '1.2em',
                fontWeight: '700',
                color: '#15803d'
              }}>
                {firstStepVisits > 0
                  ? ((funnelSteps[funnelSteps.length - 1].nb_visits / firstStepVisits) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
            <div style={{
              marginTop: '8px',
              fontSize: '0.85em',
              color: '#166534'
            }}>
              De "{funnelSteps[0]?.label}" à "{funnelSteps[funnelSteps.length - 1]?.label}"
              <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                ({formatNumber(funnelSteps[0]?.nb_visits || 0)} → {formatNumber(funnelSteps[funnelSteps.length - 1]?.nb_visits || 0)} visites)
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatomoFunnelChart;
